import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  useTheme,
  IconButton,
  alpha,
  Fade,
} from "@mui/material";
import { Phone, Language, Close } from "@mui/icons-material";

const FlagBanner = () => {
  const theme = useTheme();
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const scrollTimeoutRef = useRef(null);
  const closeTimeoutRef = useRef(null);

  // Show on scroll start
  useEffect(() => {
    const handleScroll = () => {
      // Clear any existing hide timeout
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }

      // Show banner when scrolling starts
      if (!isVisible) {
        setIsVisible(true);
      }

      // Set timeout to hide after scrolling stops
      closeTimeoutRef.current = setTimeout(() => {
        if (!isHovered) {
          setIsVisible(false);
        }
      }, 1500); // Hide 1.5 seconds after scrolling stops
    };

    // Use passive scroll listener for better performance
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Cleanup
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, [isVisible, isHovered]);

  // Manual close with localStorage
  const handleClose = () => {
    setIsVisible(false);
    // Optional: Save to localStorage if you want permanent close
    // localStorage.setItem("flagBannerClosed", "true");
  };

  // Optional: Show on initial page load for a few seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);

      // Auto hide after 5 seconds on initial load
      const hideTimer = setTimeout(() => {
        if (!isHovered) {
          setIsVisible(false);
        }
      }, 5000);

      return () => clearTimeout(hideTimer);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  if (!isVisible) return null;

  return (
    <Fade in={isVisible} timeout={300}>
      <Box
        onMouseEnter={() => {
          setIsHovered(true);
          if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
          }
        }}
        onMouseLeave={() => {
          setIsHovered(false);
          // Start hide timer when mouse leaves (if not scrolling)
          closeTimeoutRef.current = setTimeout(() => {
            setIsVisible(false);
          }, 2000);
        }}
        sx={{
          position: "fixed",
          top: 120,
          right: 20,
          zIndex: 1501,
          background: `linear-gradient(135deg, 
            ${alpha("#ffffff", 0.15)} 0%, 
            ${alpha("#f8f9fa", 0.1)} 100%
          )`,
          borderRadius: "12px",
          boxShadow: `
            0 2px 12px ${alpha("#000", 0.1)},
            inset 0 0 0 1px ${alpha("#fff", 0.3)}
          `,
          px: 2,
          py: 1.2,
          backdropFilter: "blur(16px) saturate(180%)",
          border: `1px solid ${alpha("#e9ecef", 0.4)}`,
          transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
          maxWidth: 320,
          minWidth: 280,
          opacity: isHovered ? 0.95 : 0.85,
          transform: isHovered ? "translateX(-8px)" : "translateX(0)",
          "&:hover": {
            background: `linear-gradient(135deg, 
              ${alpha("#ffffff", 0.25)} 0%, 
              ${alpha("#f8f9fa", 0.2)} 100%
            )`,
            boxShadow: `
              0 4px 20px ${alpha("#000", 0.15)},
              inset 0 0 0 1px ${alpha("#fff", 0.4)}
            `,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {/* Language Icon */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              borderRadius: "10px",
              background: `linear-gradient(135deg, 
                ${alpha("#495057", 0.08)} 0%, 
                ${alpha("#6c757d", 0.05)} 100%
              )`,
              color: "#212529",
              border: `1px solid ${alpha("#e9ecef", 0.5)}`,
              backdropFilter: "blur(8px)",
              flexShrink: 0,
            }}
          >
            <Language sx={{ fontSize: "1.2rem" }} />
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: "#212529",
                fontWeight: 600,
                mb: 0.8,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: "0.85rem",
                letterSpacing: "0.1px",
              }}
            >
              🌍 Global Support
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {/* India Number */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    minWidth: "80px",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#495057",
                      fontWeight: 600,
                      fontSize: "0.7rem",
                    }}
                  >
                    INDIA
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.6,
                    flex: 1,
                  }}
                >
                  <Phone
                    sx={{
                      fontSize: "0.8rem",
                      color: "#495057",
                      opacity: 0.8,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#212529",
                      fontWeight: 500,
                      fontSize: "0.8rem",
                      fontFamily: "'Roboto Mono', monospace",
                      letterSpacing: "0.2px",
                    }}
                  >
                    +91 70198 54530
                  </Typography>
                </Box>
              </Box>

              {/* USA Number */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    minWidth: "80px",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#495057",
                      fontWeight: 600,
                      fontSize: "0.7rem",
                    }}
                  >
                    USA
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.6,
                    flex: 1,
                  }}
                >
                  <Phone
                    sx={{
                      fontSize: "0.8rem",
                      color: "#495057",
                      opacity: 0.8,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#212529",
                      fontWeight: 500,
                      fontSize: "0.8rem",
                      fontFamily: "'Roboto Mono', monospace",
                      letterSpacing: "0.2px",
                    }}
                  >
                    +1 408-320-7170
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Close Button */}
          <IconButton
            size="small"
            onClick={handleClose}
            sx={{
              color: alpha("#6c757d", 0.5),
              alignSelf: "flex-start",
              mt: -0.5,
              transition: "all 0.2s ease",
              "&:hover": {
                color: "#495057",
                backgroundColor: alpha("#495057", 0.06),
              },
              width: 24,
              height: 24,
              minWidth: 24,
            }}
          >
            <Close sx={{ fontSize: "0.9rem" }} />
          </IconButton>
        </Box>
      </Box>
    </Fade>
  );
};

export default FlagBanner;
